import { materialInput } from './materialized.js';
import { turns } from './model.js';
import { progressModel } from './progress.js';
export const RUN_PHASE_LABEL = {
    syncing: 'Syncing…',
    offline: 'Offline',
    'not-started': 'Not started',
    working: 'Working',
    'making-progress': 'Making progress',
    stalled: 'Stalled',
    looping: 'Looping',
    'blocked-environment': 'Blocked · environment',
    'paused-limit': 'Paused · usage limit',
    waiting: 'Waiting for you',
    idle: 'Idle · turn finished',
    finished: 'Finished',
    review: 'Ready to review',
    ready: 'Ready to land',
    landed: 'Landed',
    stopped: 'Stopped',
    failed: 'Failed',
};
/** Status glyph/tone for the header pill (shape + colour + the label above). */
const ITEM_STATE = {
    syncing: 'pending',
    offline: 'stale',
    'not-started': 'pending',
    working: 'streaming',
    'making-progress': 'streaming',
    stalled: 'awaiting-user',
    looping: 'awaiting-user',
    'blocked-environment': 'awaiting-user',
    'paused-limit': 'pending',
    waiting: 'awaiting-user',
    idle: 'pending',
    finished: 'ok',
    review: 'ok',
    ready: 'ok',
    landed: 'ok',
    stopped: 'cancelled',
    failed: 'error',
};
const TONE = {
    syncing: 'unknown',
    offline: 'unknown',
    'not-started': 'unknown',
    working: 'unknown',
    'making-progress': 'ok',
    stalled: 'warn',
    looping: 'warn',
    'blocked-environment': 'warn',
    'paused-limit': 'unknown',
    waiting: 'warn',
    idle: 'unknown',
    finished: 'unknown',
    review: 'ok',
    ready: 'ok',
    landed: 'ok',
    stopped: 'unknown',
    failed: 'warn',
};
/** "5:00 AM" in the viewer's locale (the reset instant is absolute). */
export function formatResumeTime(t) {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(t);
}
export function runStateInput(view) {
    // A windowed (or not yet loaded) transcript: the materialized state is the exact source.
    if (view.material)
        return materialInput(view.material);
    const all = turns(view);
    const last = all.at(-1);
    return {
        progress: view.progress,
        status: view.status,
        turnOpen: view.status === 'running' ||
            view.status === 'compacting' ||
            (view.status !== 'error' && !!last && !last.outcome),
        awaiting: view.awaiting,
        turns: all.length + (view.turnBase ?? 0),
        ...(last?.outcome ? { lastOutcome: last.outcome } : {}),
        ...(view.activity.lastEventAt ? { lastActivity: view.activity.lastEventAt } : {}),
        ...(view.usageLimit && ['parked', 'resuming', 'failed'].includes(view.usageLimit.state)
            ? {
                usageLimit: {
                    state: view.usageLimit.state,
                    resumeAt: view.usageLimit.resumeAt,
                    resetSource: view.usageLimit.resetSource,
                    provider: view.usageLimit.provider,
                    ...(view.usageLimit.timeZone ? { timeZone: view.usageLimit.timeZone } : {}),
                    ...(view.usageLimit.error ? { error: view.usageLimit.error } : {}),
                },
            }
            : {}),
    };
}
const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;
export function runStateModel(input, o) {
    const p = input.progress;
    const m = progressModel(p, {
        now: o.now,
        ...(o.cursor != null ? { cursor: o.cursor } : {}),
        ...(input.lastActivity ? { lastActivity: input.lastActivity } : {}),
        // Silence we cannot see yet (replay/reconnect) is not the agent's silence.
        busy: input.turnOpen && !o.syncing,
    });
    const commits = p.git?.ahead ?? p.commits.length;
    const commitText = `${plural(commits, 'commit')}, not landed`;
    let phase;
    let reason;
    if (o.offline) {
        phase = 'offline';
        reason = 'Disconnected. Showing the last observed snapshot.';
    }
    else if (p.landed || o.landing === 'landed') {
        phase = 'landed';
        reason = p.landed
            ? `Landing pipeline verified ${p.landed.sha?.slice(0, 8) ?? 'the merge'}.`
            : 'Landed on the target branch.';
    }
    else if (input.awaiting > 0 || p.asks.length > 0) {
        phase = 'waiting';
        const n = Math.max(input.awaiting, p.asks.length);
        reason = `${n} unanswered ${n === 1 ? 'question or approval' : 'questions or approvals'}.`;
    }
    else if (input.usageLimit?.state === 'failed') {
        phase = 'blocked-environment';
        reason = `Could not resume after the ${input.usageLimit.provider} usage limit${input.usageLimit.error ? `: ${input.usageLimit.error}` : ''}. Resume now or switch account.`;
    }
    else if (input.usageLimit && !input.turnOpen) {
        phase = 'paused-limit';
        const at = formatResumeTime(input.usageLimit.resumeAt);
        reason =
            input.usageLimit.state === 'resuming'
                ? 'Resuming after the usage limit reset…'
                : input.usageLimit.resetSource === 'fallback'
                    ? `Paused on the ${input.usageLimit.provider} usage limit; Viko checks again at ${at} and resumes automatically.`
                    : `Paused until ${at} — resumes automatically.`;
    }
    else if (input.turnOpen) {
        phase =
            m.state === 'stalled' || m.state === 'looping' || m.state === 'blocked-environment'
                ? m.state
                : m.state === 'making-progress' || m.state === 'ready'
                    ? 'making-progress'
                    : 'working';
        reason =
            phase === 'working' && o.syncing
                ? 'A turn is in progress. Catching up on its events.'
                : phase === 'making-progress' && m.state === 'ready'
                    ? 'Recipe verified at this HEAD; the agent is still working.'
                    : m.reason;
    }
    else if (input.status === 'error' || input.lastOutcome === 'error') {
        phase = 'failed';
        reason = 'The last turn ended with an error. Send a message to retry or continue.';
    }
    else if (input.lastOutcome === 'interrupted') {
        phase = 'stopped';
        reason = 'The last turn was interrupted. Send a message to continue.';
    }
    else if (!input.turns && !p.startedAt) {
        phase = 'not-started';
        reason = 'No turn has started yet.';
    }
    else if (m.state === 'ready') {
        phase = 'ready';
        reason = m.reason;
    }
    else if (m.state === 'blocked-environment') {
        phase = 'blocked-environment';
        reason = m.reason;
    }
    else if (o.ended) {
        phase = commits > 0 ? 'review' : 'finished';
        reason =
            commits > 0
                ? `${commitText}. Review the branch evidence, then land.`
                : 'The session ended without commits.';
    }
    else {
        phase = 'idle';
        reason =
            commits > 0
                ? `${commitText}: ready to review. Awaiting your next message.`
                : 'Turn finished. Awaiting your next message.';
    }
    return {
        phase,
        label: RUN_PHASE_LABEL[phase],
        reason,
        tone: TONE[phase],
        itemState: ITEM_STATE[phase],
        syncing: !!o.syncing,
        progress: m,
    };
}
/** Replay without a cached snapshot: no verdict until the history has caught up. */
export function syncingModel(input, o) {
    const m = progressModel(input.progress, {
        now: o.now,
        ...(o.cursor != null ? { cursor: o.cursor } : {}),
        busy: false,
    });
    return {
        phase: 'syncing',
        label: RUN_PHASE_LABEL.syncing,
        reason: 'Loading this session’s history. The verdict appears once it has caught up.',
        tone: 'unknown',
        itemState: 'pending',
        syncing: true,
        progress: m,
    };
}
//# sourceMappingURL=run-state.js.map